import React, { useState, useRef, useEffect } from 'react';
import { X, RotateCw, Crop, Type, Pen, Trash2, Undo } from 'lucide-react';
import { toast } from 'sonner';

const ImageEditorModal = ({ imageData, onClose, onSave, returnBothImages = false }) => {
  const [rotation, setRotation] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawColor, setDrawColor] = useState('#FF0000');
  const [drawMode, setDrawMode] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [textElements, setTextElements] = useState([]);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [pinchStart, setPinchStart] = useState(null);
  const [cropMode, setCropMode] = useState(false);
  const [cropStart, setCropStart] = useState(null);
  const [cropEnd, setCropEnd] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canvasHistory, setCanvasHistory] = useState([]);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const lastPoint = useRef(null);
  const containerRef = useRef(null);

  // Load image onto canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Cap canvas size to max 1600px on longest side to prevent OOM crashes on high-res phone photos
      const MAX_DIM = 1600;
      let w = img.width;
      let h = img.height;
      if (w > MAX_DIM || h > MAX_DIM) {
        const scale = MAX_DIM / Math.max(w, h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      canvas.width = w;
      canvas.height = h;
      
      ctx.drawImage(img, 0, 0, w, h);
      setImageLoaded(true);
      imageRef.current = img;
    };
    
    img.onerror = () => {
      toast.error('Failed to load image');
      setImageLoaded(false);
    };
    
    img.src = imageData;
  }, [imageData]);

  // Redraw canvas after rotation
  const redrawCanvas = (rotationDegrees) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;
    
    if (!img) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Save context state
    ctx.save();
    
    // Rotate around center
    if (rotationDegrees !== 0) {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      ctx.translate(centerX, centerY);
      ctx.rotate((rotationDegrees * Math.PI) / 180);
      ctx.translate(-centerX, -centerY);
    }
    
    // Draw rotated image
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    
    // Restore context
    ctx.restore();
  };

  const handleRotate = () => {
    saveCanvasState(); // Save state before rotating
    const newRotation = (rotation + 90) % 360;
    setRotation(newRotation);
    redrawCanvas(newRotation);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    try {
      if (canvas && imageLoaded) {
        // Render all text elements onto canvas before saving
        const ctx = canvas.getContext('2d');
        textElements.forEach(textEl => {
          ctx.font = `${textEl.fontSize}px Poppins, sans-serif`;
          ctx.fillStyle = textEl.color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(textEl.text, textEl.x, textEl.y);
        });
        
        const editedImage = canvas.toDataURL('image/jpeg', 0.85);
        
        // Return format based on prop
        if (returnBothImages) {
          onSave({ original: imageData, edited: editedImage });
        } else {
          onSave(editedImage);
        }
      } else {
        // If no edits made
        if (returnBothImages) {
          onSave({ original: imageData, edited: imageData });
        } else {
          onSave(imageData);
        }
      }
    } catch (e) {
      console.error('Error saving image from canvas:', e);
      toast.error('Could not process image. Please try again.');
      // Fall back to original image so user doesn't lose progress
      if (returnBothImages) {
        onSave({ original: imageData, edited: imageData });
      } else {
        onSave(imageData);
      }
    }
  };

  // Get coordinates from mouse or touch event
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const x = (clientX - rect.left) * (canvas.width / rect.width);
    const y = (clientY - rect.top) * (canvas.height / rect.height);
    
    return { x, y };
  };

  const handleStart = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);

    if (cropMode) {
      setCropStart({ x, y });
      setCropEnd(null);
    } else if (drawMode) {
      // Save state before starting to draw
      if (!isDrawing) {
        saveCanvasState();
      }
      setIsDrawing(true);
      lastPoint.current = { x, y };
    }
  };

  const handleMove = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);

    if (cropMode && cropStart) {
      setCropEnd({ x, y });
    } else if (drawMode && isDrawing && lastPoint.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.strokeStyle = drawColor;
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
      ctx.lineTo(x, y);
      ctx.stroke();
      lastPoint.current = { x, y };
    }
  };

  const handleEnd = (e) => {
    e.preventDefault();
    if (cropMode && cropStart && cropEnd) {
      applyCrop();
    }
    setIsDrawing(false);
    lastPoint.current = null;
  };

  const saveCanvasState = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      try {
        // Save current canvas as data URL
        const state = {
          dataURL: canvas.toDataURL('image/jpeg', 0.7),
          width: canvas.width,
          height: canvas.height,
        };
        setCanvasHistory(prev => [...prev, state]);
      } catch (e) {
        console.warn('Could not save canvas state for undo:', e);
      }
    }
  };

  const undoCrop = () => {
    if (canvasHistory.length === 0) {
      toast.error('Nothing to undo');
      return;
    }

    const lastState = canvasHistory[canvasHistory.length - 1];
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = lastState.width;
      canvas.height = lastState.height;
      ctx.drawImage(img, 0, 0);
      setCanvasHistory(prev => prev.slice(0, -1));
      toast.success('Undo successful');
    };

    img.src = lastState.dataURL;
  };

  const applyCrop = () => {
    if (!cropStart || !cropEnd) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const x = Math.min(cropStart.x, cropEnd.x);
    const y = Math.min(cropStart.y, cropEnd.y);
    const width = Math.abs(cropEnd.x - cropStart.x);
    const height = Math.abs(cropEnd.y - cropStart.y);

    if (width < 10 || height < 10) {
      toast.error('Crop area too small');
      setCropStart(null);
      setCropEnd(null);
      return;
    }

    // Save current state before cropping
    saveCanvasState();

    // Get cropped image data
    const imageData = ctx.getImageData(x, y, width, height);
    
    // Resize canvas to cropped dimensions
    canvas.width = width;
    canvas.height = height;
    
    // Draw cropped image
    ctx.putImageData(imageData, 0, 0);
    
    setCropMode(false);
    setCropStart(null);
    setCropEnd(null);
    toast.success('Image cropped - Use Undo to revert');
  };

  const addText = () => {
    if (!textInput.trim()) {
      toast.error('Please enter text');
      return;
    }
    
    const newText = {
      id: Date.now(),
      text: textInput,
      x: canvasRef.current.width / 2,
      y: canvasRef.current.height / 2,
      fontSize: 36,
      color: drawColor,
    };
    
    setTextElements([...textElements, newText]);
    setTextInput('');
    setTextMode(false);
    toast.success('Text added - Drag to move, pinch to resize');
  };

  const handleTextTouchStart = (e, textId) => {
    e.stopPropagation();
    setSelectedTextId(textId);
    
    if (e.touches.length === 1) {
      // Single touch - drag
      const touch = e.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      setDragStart({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      });
    } else if (e.touches.length === 2) {
      // Two touches - pinch to resize
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      setPinchStart(distance);
    }
  };

  const handleTextTouchMove = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!selectedTextId) return;

    if (e.touches.length === 1 && dragStart) {
      // Drag - clamp within canvas bounds
      const touch = e.touches[0];
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const canvas = canvasRef.current;
      
      let x = (touch.clientX - canvasRect.left) * (canvas.width / canvasRect.width);
      let y = (touch.clientY - canvasRect.top) * (canvas.height / canvasRect.height);
      
      // Clamp position within canvas bounds with padding
      const padding = 50; // Minimum distance from edge
      x = Math.max(padding, Math.min(canvas.width - padding, x));
      y = Math.max(padding, Math.min(canvas.height - padding, y));
      
      setTextElements(textElements.map(t => 
        t.id === selectedTextId ? { ...t, x, y } : t
      ));
    } else if (e.touches.length === 2) {
      // Pinch to resize - always recalculate distance
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      if (pinchStart) {
        const scale = distance / pinchStart;
        setTextElements(textElements.map(t => 
          t.id === selectedTextId 
            ? { ...t, fontSize: Math.max(8, Math.min(300, t.fontSize * scale)) }
            : t
        ));
      }
      setPinchStart(distance);
    }
  };

  const handleTextTouchEnd = (e) => {
    e.stopPropagation();
    setDragStart(null);
    setPinchStart(null);
    // Keep selectedTextId so text stays selected
  };

  const deleteText = (textId) => {
    setTextElements(textElements.filter(t => t.id !== textId));
    toast.success('Text removed');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[110] p-4" data-testid="image-editor-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-amber-100 to-orange-100 p-4 flex justify-between items-center border-b-2 border-amber-200">
          <h2 className="text-xl font-bold text-gray-800">Edit Photo</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-full transition-colors"
            data-testid="close-editor"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {/* Image Preview Canvas with Text Overlays */}
          <div className="mb-6 flex justify-center bg-gray-100 rounded-xl p-2 sm:p-4 overflow-hidden touch-none" ref={containerRef}>
            <div className="relative inline-block max-w-full">
              <canvas
                ref={canvasRef}
                className={`max-w-full max-h-[500px] object-contain rounded-lg shadow-lg touch-none ${
                  cropMode ? 'cursor-crosshair' : drawMode ? 'cursor-crosshair' : 'cursor-default'
                }`}
                onMouseDown={handleStart}
                onMouseMove={handleMove}
                onMouseUp={handleEnd}
                onMouseLeave={() => {
                  setIsDrawing(false);
                  lastPoint.current = null;
                }}
                onTouchStart={handleStart}
                onTouchMove={handleMove}
                onTouchEnd={handleEnd}
                data-testid="editor-canvas"
              />
              
              {/* Crop selection overlay */}
              {cropMode && cropStart && cropEnd && (
                <div
                  className="absolute border-2 border-dashed border-blue-500 bg-blue-200 bg-opacity-30 pointer-events-none"
                  style={{
                    left: `${Math.min(cropStart.x, cropEnd.x) * (canvasRef.current?.getBoundingClientRect().width / canvasRef.current?.width)}px`,
                    top: `${Math.min(cropStart.y, cropEnd.y) * (canvasRef.current?.getBoundingClientRect().height / canvasRef.current?.height)}px`,
                    width: `${Math.abs(cropEnd.x - cropStart.x) * (canvasRef.current?.getBoundingClientRect().width / canvasRef.current?.width)}px`,
                    height: `${Math.abs(cropEnd.y - cropStart.y) * (canvasRef.current?.getBoundingClientRect().height / canvasRef.current?.height)}px`,
                  }}
                />
              )}
              
              {/* Text overlays - draggable and resizable */}
              {textElements.map(textEl => {
                const canvasRect = canvasRef.current?.getBoundingClientRect();
                const canvasWidth = canvasRef.current?.width || 1;
                const canvasHeight = canvasRef.current?.height || 1;
                const scale = canvasRect ? canvasRect.width / canvasWidth : 1;
                
                // Clamp text position within canvas bounds
                const padding = 50 * scale; // Padding from edges
                const leftPos = Math.max(padding, Math.min(textEl.x * scale, (canvasRect?.width || 0) - padding));
                const topPos = Math.max(padding, Math.min(textEl.y * scale, (canvasRect?.height || 0) - padding));
                
                return (
                  <div
                    key={textEl.id}
                    className={`absolute touch-none select-none cursor-move ${selectedTextId === textEl.id ? 'ring-2 ring-blue-500 rounded' : ''}`}
                    style={{
                      left: `${leftPos}px`,
                      top: `${topPos}px`,
                      transform: 'translate(-50%, -50%)',
                      fontSize: `${textEl.fontSize * scale}px`,
                      color: textEl.color,
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 'bold',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                      WebkitTextStroke: '1px rgba(0,0,0,0.3)',
                      padding: '8px',
                      zIndex: 10,
                      maxWidth: `${(canvasRect?.width || 500) - 100}px`,
                      wordWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                    }}
                    onTouchStart={(e) => handleTextTouchStart(e, textEl.id)}
                    onTouchMove={handleTextTouchMove}
                    onTouchEnd={handleTextTouchEnd}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setSelectedTextId(textEl.id);
                    }}
                  >
                    {textEl.text}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteText(textEl.id);
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      style={{ fontSize: '12px' }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Editor Tools */}
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                type="button"
                onClick={handleRotate}
                disabled={!imageLoaded}
                className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-amber-100 hover:bg-amber-200 active:bg-amber-300 rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base"
                data-testid="rotate-button"
              >
                <RotateCw className="w-4 h-4" />
                <span className="hidden sm:inline">Rotate 90°</span>
                <span className="sm:hidden">Rotate</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setCropMode(!cropMode);
                  setDrawMode(false);
                  setTextMode(false);
                  setCropStart(null);
                  setCropEnd(null);
                }}
                disabled={!imageLoaded}
                className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base ${
                  cropMode ? 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700' : 'bg-green-100 hover:bg-green-200 active:bg-green-300'
                }`}
                data-testid="crop-button"
              >
                <Crop className="w-4 h-4" />
                {cropMode ? 'Cropping' : 'Crop'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setDrawMode(!drawMode);
                  setCropMode(false);
                  setTextMode(false);
                }}
                disabled={!imageLoaded}
                className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base ${
                  drawMode ? 'bg-purple-500 text-white hover:bg-purple-600 active:bg-purple-700' : 'bg-purple-100 hover:bg-purple-200 active:bg-purple-300'
                }`}
                data-testid="draw-button"
              >
                <Pen className="w-4 h-4" />
                {drawMode ? 'Drawing' : 'Draw'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setTextMode(!textMode);
                  setCropMode(false);
                  setDrawMode(false);
                }}
                disabled={!imageLoaded}
                className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base ${
                  textMode ? 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700' : 'bg-blue-100 hover:bg-blue-200 active:bg-blue-300'
                }`}
                data-testid="text-button"
              >
                <Type className="w-4 h-4" />
                <span className="hidden sm:inline">Add Text</span>
                <span className="sm:hidden">Text</span>
              </button>

              <button
                type="button"
                onClick={undoCrop}
                disabled={canvasHistory.length === 0}
                className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-orange-100 hover:bg-orange-200 active:bg-orange-300 rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base"
                data-testid="undo-button"
              >
                <Undo className="w-4 h-4" />
                Undo
              </button>

              <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 rounded-lg border border-gray-300">
                <span className="text-xs sm:text-sm font-semibold text-gray-700">Color:</span>
                <input
                  type="color"
                  value={drawColor}
                  onChange={(e) => setDrawColor(e.target.value)}
                  className="w-10 h-8 sm:w-12 sm:h-10 rounded cursor-pointer border-2 border-gray-300"
                  data-testid="color-picker"
                />
              </div>
            </div>

            {textMode && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 p-3 sm:p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <input
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Enter text to add..."
                  className="input-field flex-1 min-w-0"
                  data-testid="text-input"
                />
                <button
                  type="button"
                  onClick={addText}
                  className="btn-primary whitespace-nowrap px-4 py-2"
                  data-testid="add-text-button"
                >
                  Add Text
                </button>
              </div>
            )}

            {cropMode && (
              <div className="text-sm text-blue-700 bg-blue-50 p-3 rounded-lg border-2 border-blue-200 font-semibold">
                📐 Drag on the image to select the crop area, then release to apply
              </div>
            )}

            {drawMode && (
              <div className="text-sm text-purple-700 bg-purple-50 p-3 rounded-lg border-2 border-purple-200 font-semibold">
                ✏️ Click and drag on the image to draw
              </div>
            )}

            <div className="text-sm text-gray-600 bg-amber-50 p-3 rounded-lg">
              <p className="font-semibold mb-1">Quick Tips:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong>Rotate:</strong> Rotate image 90° clockwise</li>
                <li><strong>Crop:</strong> Click and drag to select area to keep</li>
                <li><strong>Draw:</strong> Click and drag to draw freehand</li>
                <li><strong>Add Text:</strong> Type text and it will appear on image</li>
                <li><strong>Color:</strong> Change color for text and drawing</li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
              data-testid="cancel-edit"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!imageLoaded}
              className="btn-primary flex-1 disabled:opacity-50"
              data-testid="save-edit"
            >
              Save Photo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEditorModal;
